import { cn } from '@/lib/utils'
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
} from 'lucide-react'
import { useMemo, useState } from 'react'

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue }

const PATH_SEPARATOR = ' '

export const pathKey = (path: string[]): string => path.join(PATH_SEPARATOR)

export const isExpandable = (value: JsonValue): boolean =>
  typeof value === 'object' &&
  value !== null &&
  (Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0)

const childEntries = (value: JsonValue): [string, JsonValue][] => {
  if (Array.isArray(value)) {
    return value.map((item, index) => [`[${index}]`, item])
  }
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value)
  }
  return []
}

const summarize = (value: JsonValue): string => {
  if (Array.isArray(value)) {
    return `${value.length} ${value.length === 1 ? 'item' : 'items'}`
  }
  if (typeof value === 'object' && value !== null) {
    const count = Object.keys(value).length
    return `${count} ${count === 1 ? 'field' : 'fields'}`
  }
  return ''
}

interface TreeNodeProps {
  label: string
  value: JsonValue
  path: string[]
  depth: number
  expanded: Set<string>
  selected: string
  onToggle: (key: string) => void
  onSelect: (path: string[]) => void
}

const TreeNode: React.FC<TreeNodeProps> = ({
  label,
  value,
  path,
  depth,
  expanded,
  selected,
  onToggle,
  onSelect,
}) => {
  const key = pathKey(path)
  const expandable = isExpandable(value)
  const isOpen = expanded.has(key)
  const isSelected = selected === key
  const entries = expandable && isOpen ? childEntries(value) : []

  return (
    <div>
      <div
        className={cn(
          'flex cursor-pointer items-center gap-1.5 rounded-md py-1.5 pr-2 text-sm transition-colors',
          isSelected ? 'bg-secondary text-primary' : 'hover:bg-accent'
        )}
        style={{ paddingLeft: depth * 14 + 4 }}
        onClick={() => {
          onSelect(path)
          if (expandable) onToggle(key)
        }}
      >
        {expandable ? (
          isOpen ? (
            <ChevronDown className="size-3.5 shrink-0 opacity-60" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 opacity-60" />
          )
        ) : (
          <span className="inline-block size-3.5 shrink-0" />
        )}
        {expandable ? (
          isOpen ? (
            <FolderOpen className="size-4 shrink-0 text-amber-500" />
          ) : (
            <Folder className="size-4 shrink-0 text-amber-500" />
          )
        ) : (
          <FileText className="size-4 shrink-0 opacity-50" />
        )}
        <span className="truncate" title={label}>
          {label}
        </span>
        {expandable && (
          <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">
            {summarize(value)}
          </span>
        )}
      </div>
      {entries.length > 0 && (
        <div>
          {entries.map(([childLabel, childValue]) => (
            <TreeNode
              key={pathKey([...path, childLabel])}
              label={childLabel}
              value={childValue}
              path={[...path, childLabel]}
              depth={depth + 1}
              expanded={expanded}
              selected={selected}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface JsonTreeProps {
  data: JsonValue
  selected: string[]
  onSelect: (path: string[]) => void
  className?: string
}

export const JsonTree: React.FC<JsonTreeProps> = ({
  data,
  selected,
  onSelect,
  className,
}) => {
  // Expand the top-level folders by default so the structure is visible.
  const initialExpanded = useMemo(() => {
    const set = new Set<string>()
    childEntries(data).forEach(([label, value]) => {
      if (isExpandable(value)) set.add(pathKey([label]))
    })
    return set
  }, [data])

  const [expanded, setExpanded] = useState<Set<string>>(initialExpanded)

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const entries = childEntries(data)
  const selectedKey = pathKey(selected)

  return (
    <div className={cn('text-foreground', className)}>
      {entries.map(([label, value]) => (
        <TreeNode
          key={pathKey([label])}
          label={label}
          value={value}
          path={[label]}
          depth={0}
          expanded={expanded}
          selected={selectedKey}
          onToggle={toggle}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}
